import {
  ApiResponse,
  ApisauceConfig,
  ApisauceInstance,
  CLIENT_ERROR,
  CONNECTION_ERROR,
  NETWORK_ERROR,
  SERVER_ERROR,
  TIMEOUT_ERROR,
} from 'apisauce';
import { AxiosRequestConfig } from 'axios';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ClientConfig } from '~client/client-config';
import { extractArrayParams } from '~client/controller/utils';
import { ClientError } from '~client/error/client-error';
import { ClientNetworkError } from '~client/error/client-network-error';
import { ClientRequestError } from '~client/error/client-request-error';
import { ClientServerError } from '~client/error/client-server-error';
import { ClientTimeoutError } from '~client/error/client-timeout-error';
import { ClientUnauthorizedError } from '~client/error/client-unauthorized-error';

export class BaseController {
  protected api: ApisauceInstance;
  protected config: ClientConfig;

  constructor(api: ApisauceInstance, config?: ApisauceConfig) {
    this.api = api;
    this.config = config;
  }

  protected async del<T>(url: string, params?: {}, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(url, params, config);
    return this.extractResponseBody(response);
  }

  protected async get<T>(url: string, params?: {}, config?: AxiosRequestConfig): Promise<T> {
    const { url: fixedUrl, params: fixedParams } = extractArrayParams(url, params);
    const response = await this.api.get<T>(fixedUrl, fixedParams, config);
    return this.extractResponseBody(response);
  }

  protected async post<T>(url: string, params?: {}, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, params, config);
    return this.extractResponseBody(response);
  }

  protected async put<T>(url: string, params?: {}, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(url, params, config);
    return this.extractResponseBody(response);
  }

  /*
   @deprecated favor using non-observable function instead
    */
  protected del$<T>(url: string, params?: {}, config?: AxiosRequestConfig): Observable<T> {
    return this.mapPromise(this.api.delete<T>(url, params, config));
  }

  /*
   @deprecated favor using non-observable function instead
    */
  protected get$<T>(url: string, params?: {}, config?: AxiosRequestConfig): Observable<T> {
    const { url: fixedUrl, params: fixedParams } = extractArrayParams(url, params);
    return this.mapPromise(this.api.get<T>(fixedUrl, fixedParams, config));
  }

  /*
   @deprecated favor using non-observable function instead
    */
  protected post$<T>(url: string, params?: {}, config?: AxiosRequestConfig): Observable<T> {
    return this.mapPromise(this.api.post<T>(url, params, config));
  }

  /*
  @deprecated favor using non-observable function instead
   */
  protected put$<T>(url: string, params?: {}, config?: AxiosRequestConfig): Observable<T> {
    return this.mapPromise(this.api.put<T>(url, params, config));
  }

  protected mapPromise<T>(promise: Promise<ApiResponse<T>>): Observable<T> {
    return from(promise).pipe(
      map((response: ApiResponse<T>) => this.extractResponseBody(response)),
    );
  }

  protected extractResponseBody<T>(response: ApiResponse<T>): T {
    const { status } = response;

    if (status === 401) {
      this.handleUnauthorized();
    } else if (!response.ok) {
      throw this.mapProblemToError(response);
    }

    this.config.onExtractResponseBody?.(response);

    if (!response.data) {
      // in case no data is returned, the result will not be undefined/null
      return {} as T;
    }
    return response.data;
  }

  protected setAuthorization(token: string): void {
    this.api.setHeader('Authorization', `Bearer ${token}`);
  }

  protected removeAuthorization(): void {
    this.api.deleteHeader('Authorization');
  }

  protected handleUnauthorized(): void {
    this.config.unauthorizedCallback?.();
    throw new ClientUnauthorizedError();
  }

  protected mapProblemToError<T>(response: ApiResponse<T>): ClientError {
    const { problem } = response;

    if (problem === CLIENT_ERROR) {
      return new ClientRequestError<T>(response.status, response.data);
    }

    if (problem === SERVER_ERROR) {
      return new ClientServerError<T>(response.status, response.data);
    }

    if (problem === CONNECTION_ERROR || problem === NETWORK_ERROR) {
      return new ClientNetworkError();
    }

    if (problem === TIMEOUT_ERROR) {
      return new ClientTimeoutError();
    }

    return new ClientError();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src332225586/src/modules/client/src/controller/base-controller.ts